import {Component, Inject, OnInit, inject, ChangeDetectionStrategy, ViewChild, ElementRef, AfterViewInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable} from 'rxjs';
import {WeatherRisk, WeatherRiskService} from '../../service/weather-risk.service';
import moment from 'moment';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

@Component({
  selector: 'app-trip-weather-risk-modal',
  templateUrl: './trip-weather-risk-modal.component.html',
  styleUrl: './trip-weather-risk-modal.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Default,
})
export class TripWeatherRiskModalComponent implements OnInit, AfterViewInit  {
  @ViewChild('tripTipsPdfContent', { static: false }) tripTipsPdfContent!: ElementRef<HTMLDivElement>;
  private dialogRef = inject(MatDialogRef<TripWeatherRiskModalComponent>);
  private weatherRiskService = inject(WeatherRiskService);
  public weatherRisk$!: Observable<WeatherRisk[]>;
  public isLoading: boolean = true;

constructor(
    @Inject(MAT_DIALOG_DATA) public tripId: number
  ) {  }
  ngOnInit(): void {
    this.weatherRisk$ = this.weatherRiskService.getWeatherRiskByTripId(this.tripId);
    this.weatherRisk$.subscribe({
        next: () => this.isLoading = false,
        error: () => {
            this.isLoading = false;
        }
    });
  }

  ngAfterViewInit(): void {
    console.log(this.tripTipsPdfContent.nativeElement);
  }

  handleClose(): void {
    this.dialogRef.close();
  }

  getRiskLevelClass(riskLevel: 'Low' | 'Medium' | 'High'): string {
    return `risk-level-${riskLevel.toLowerCase()}`;
  }

  public formatTravelPeriod(rawPeriod: string): { date: string; timeRange: string } | null {
    if (!rawPeriod || typeof rawPeriod !== 'string') {
      return null;
    }

    const parts = rawPeriod.split(' to ');
    if (parts.length !== 2) {
      return null;
    }

    const [startIso, endIso] = parts;

    // Use Moment.js to parse the ISO 8601 strings
    const startMoment = moment(startIso);
    const endMoment = moment(endIso);

    // Define the formats for display
    const DATE_FORMAT = 'MMM DD, YYYY'; // e.g., Nov 27, 2025
    const TIME_FORMAT = 'h:mm A';       // e.g., 4:00 AM

    // --- 1. Determine the Date Component ---
    let dateDisplay: string;

    // Check if the start and end dates are the same (ignoring time)
    if (startMoment.isSame(endMoment, 'day')) {
      dateDisplay = startMoment.format(DATE_FORMAT);
    } else {
      // Multi-day trip
      dateDisplay = `${startMoment.format(DATE_FORMAT)} - ${endMoment.format(DATE_FORMAT)}`;
    }

    // --- 2. Determine the Time Component ---
    const startTime = startMoment.format(TIME_FORMAT);
    const endTime = endMoment.format(TIME_FORMAT);

    let timeRangeDisplay: string;
    
    if (startTime === endTime && startMoment.isSame(endMoment, 'minute')) {
        // If the start and end times/minutes are identical (highly unlikely for a travel period)
        timeRangeDisplay = startTime;
    } else {
        timeRangeDisplay = `${startTime} - ${endTime}`;
    }
    
    return {
      date: dateDisplay,
      timeRange: timeRangeDisplay
    };
  }

  public getStepStatusClass(status: string): string {
    if (!status) return '';
    const s = status.toLowerCase();
    if (s.includes('time')) return 'status-on-time';
    if (s.includes('delay')) return 'status-delayed';
    if (s.includes('cancel')) return 'status-cancelled';
    return '';
  }

  public downloadTravelTips() {
    this.isLoading = true;
    const data = this.tripTipsPdfContent.nativeElement;

    // Use html2canvas options to capture the full scrollable area
    html2canvas(data, {
      scale: 2,
      useCORS: true,
      logging: false,
      scrollY: -window.scrollY, // Reset scroll position for the snapshot
      windowHeight: data.scrollHeight, // Force capture of the hidden area
      height: data.scrollHeight,
      width: data.scrollWidth
    }).then(canvas => {
      const imgWidth = 208; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const contentDataURL = canvas.toDataURL('image/png');
      
      // Check if the content is taller than one A4 page
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageHeight = 295; // A4 height in mm
      let heightLeft = imgHeight;
      let position = 0;

      // First Page
      pdf.addImage(contentDataURL, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add extra pages if the content is long (Travel Tips usually are!)
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(contentDataURL, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('Travel_Tips.pdf');
      this.isLoading = false;
    }).catch(err => {
      console.error("PDF Generation Error:", err);
      this.isLoading = false;
    });
  }
}
